import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../store';
import {
  Box,
  Button,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  Divider,
  Paper,
  Alert,
  Skeleton,
  Tooltip,
  Fade,
  Chip,
  Card,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Stack,
  Avatar,
  Badge,
  Tabs,
  Tab,
  InputAdornment,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Checkbox,
  Fab,
  FormControlLabel,
  FormGroup,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  GridView as GridViewIcon,
  List as ListViewIcon,
  MoreVert as MoreVertIcon,
  FileUpload as FileUploadIcon,
  CreateNewFolder as CreateNewFolderIcon,
  SortByAlpha as SortByAlphaIcon,
  SortOutlined as SortOutlinedIcon,
  SelectAll as SelectAllIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  FolderOutlined as FolderOutlinedIcon,
  Folder as FolderIcon,
  DescriptionOutlined as FileOutlinedIcon,
  ImageOutlined as ImageOutlinedIcon,
  PictureAsPdfOutlined as PdfOutlinedIcon,
  VideoFileOutlined as VideoOutlinedIcon,
  MusicNoteOutlined as AudioOutlinedIcon,
  FormatShapesOutlined as OtherFilesOutlinedIcon,
  CloudDownloadOutlined as DownloadOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlinedIcon,
  DriveFileMoveOutlined as MoveOutlinedIcon,
  ContentCopyOutlined as CopyOutlinedIcon,
  StarOutline as StarOutlineIcon,
  StarRate as StarRateIcon,
  ViewCompactOutlined as CompactViewIcon,
  FilterAltOff as ClearFilterIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  GroupWork as GroupWorkIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RootState } from '../store';
import { 
  selectAll,
  fetchFolderContentsSuccess,
  fetchFolderContentsStart,
  fetchFolderContentsFailure,
  fetchFolderPathSuccess,
  setCurrentFolder,
  clearFolderContents
} from '../store/slices/folderSlice';
import {
  setViewMode,
  setSortBy,
  setSortDirection,
  toggleSelectionMode,
  toggleSelectItem,
  selectAllItems,
  SortBy,
  SortDirection,
  openConfirmDialog,
  clearSelection as clearUiSelection,
  openUploadDialog
} from '../store/slices/uiSlice';
import FileCard from '../components/files/FileCard';
import FileListItem from '../components/files/FileListItem';
import FolderCard from '../components/files/FolderCard';
import FolderListItem from '../components/files/FolderListItem';
import NewFolderDialog from '../components/dialogs/NewFolderDialog';
import EmptyState from '../components/ui/EmptyState';
import BatchOperationsToolbar from '../components/files/BatchOperationsToolbar';
import AdvancedSearch from '../components/ui/AdvancedSearch';
import DroppableArea from '../components/ui/DroppableArea';
import { fileService } from '../services/fileService';
import folderService from '../services/folderService';
import activityService from '../services/activityService';
import api from '../utils/api';
import { getAxiosConfig } from '../utils/axios';
import { useSnackbar } from 'notistack';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { File, Folder, Activity } from '../types';
import { Breadcrumb } from '../store/slices/folderSlice';
import websocketService from '../services/websocketService';
import config from '../config';
import filterService from '../services/filterService';
import { FileItem } from '../types/files';
import ShareDialog from '../components/share/ShareDialog';
import { 
  clearClipboard,
  ClipboardItem
} from '../utils/clipboardManager';
import { copyToClipboard, cutToClipboard } from '../utils/clipboardManager';
import useFolderDownload from '../components/file/FileCompressor';
import RenameDialog from '../components/dialogs/RenameDialog';
import ItemPropertiesDialog, { PropertiesItem } from '../components/dialogs/ItemPropertiesDialog';
import usePageTitle from '../hooks/usePageTitle';
import {
  resolveUploadFolderId,
  ROOT_UPLOAD_ERROR
} from '../utils/uploadContext';

// File type for filtering
type FileType = 'all' | 'folders' | 'documents' | 'images' | 'videos' | 'audio' | 'pdfs' | 'others';

// Add DraggedItem interface near the top of the file, after the imports
interface DraggedItem {
  id: string;
  type: 'file' | 'folder';
  name: string;
}

// WebSocket event types
const WS_EVENTS = {
  FILE_UPDATED: 'file_updated',
  FOLDER_UPDATED: 'folder_updated'
} as const;

type FilesViewMode =
  | 'recent'
  | 'favorites'
  | 'shared-with-me'
  | 'my-shared'
  | 'trash'
  | 'images'
  | 'videos'
  | 'documents'
  | 'audio'
  | 'archives'
  | 'code';

const VIEW_TITLES: Record<FilesViewMode, string> = {
  recent: 'Recent Files',
  favorites: 'Favorites',
  'shared-with-me': 'Shared With Me',
  'my-shared': 'My Shared Items',
  trash: 'Trash',
  images: 'Images',
  videos: 'Videos',
  documents: 'Documents',
  audio: 'Audio',
  archives: 'Archives',
  code: 'Code'
};

const FILES_VIEW_MODES = new Set<FilesViewMode>([
  'recent',
  'favorites',
  'shared-with-me',
  'my-shared',
  'trash',
  'images',
  'videos',
  'documents',
  'audio',
  'archives',
  'code'
]);

const isRequestCanceled = (error: unknown): boolean => {
  const err = error as { code?: string; name?: string };
  return (
    err?.code === 'ERR_CANCELED' ||
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError'
  );
};

const FileBrowser: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useMuiTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { folderId } = useParams<{ folderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchQuery = searchParams.get('search');
  const filterId = searchParams.get('filter');
  const viewParam = searchParams.get('view');
  const filesView = (viewParam && FILES_VIEW_MODES.has(viewParam as FilesViewMode)
    ? (viewParam as FilesViewMode)
    : null);
  const isSpecialView = Boolean(filesView);
  usePageTitle(filesView ? VIEW_TITLES[filesView] : 'My Files');

  const browseKey = useMemo(
    () => `${folderId ?? 'root'}|${filesView ?? ''}|${filterId ?? ''}`,
    [folderId, filesView, filterId]
  );

  // Get data from Redux store
  const { currentFolderContents, breadcrumbs, isLoading, currentFolder, lastBrowsedFolderId } = useSelector(
    (state: RootState) => state.folders
  );
  const { viewMode, sortBy, sortDirection, isSelectionMode, selectedItems: uiSelectedItems } = useSelector(
    (state: RootState) => state.ui
  );

  // Clipboard state from Redux
  const clipboard = useSelector((state: RootState) => state.clipboard);
  const [rootFolderId, setRootFolderId] = useState<string | null>(null);

  const effectiveUploadFolderId = useMemo(
    () =>
      resolveUploadFolderId({
        routeFolderId: folderId,
        currentFolderId: currentFolder?.id,
        filesView,
        rootFolderId,
        lastBrowsedFolderId
      }),
    [folderId, currentFolder?.id, filesView, rootFolderId, lastBrowsedFolderId]
  );

  const canUploadHere = Boolean(effectiveUploadFolderId);

  const requestUpload = useCallback(() => {
    if (effectiveUploadFolderId) {
      dispatch(openUploadDialog({ folderId: effectiveUploadFolderId }));
      return;
    }
    enqueueSnackbar(ROOT_UPLOAD_ERROR, { variant: 'error' });
    if (filesView || folderId || searchParams.get('upload')) {
      navigate('/files?upload=blocked', { replace: !filesView && !folderId });
    }
  }, [
    effectiveUploadFolderId,
    dispatch,
    enqueueSnackbar,
    filesView,
    folderId,
    searchParams,
    navigate
  ]);

  // Local state for dialogs and menus
  const [isNewFolderDialogOpen, setIsNewFolderDialogOpen] = useState(false);
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [viewOptions, setViewOptions] = useState<string>(viewMode);
  const [selectedFileType, setSelectedFileType] = useState<FileType>('all');
  const [searchText, setSearchText] = useState(searchQuery || '');
  const [recentActivity, setRecentActivity] = useState<Activity[]>([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<null | HTMLElement>(null);
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [showDeletedFiles, setShowDeletedFiles] = useState(false);
  const [showSharedFiles, setShowSharedFiles] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useState<{
    from: Date | null;
    to: Date | null;
  }>({ from: null, to: null });
  const [isActionsPanelOpen, setIsActionsPanelOpen] = useState(false);
  const [groupByType, setGroupByType] = useState(false);

  // Add new state for drag and drop
  const [isDragging, setIsDragging] = useState(false);
  const [dragTarget, setDragTarget] = useState<string | null>(null);

  // Add new state for share dialog
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [shareItem, setShareItem] = useState<{ id: string; name: string; type: 'file' | 'folder' } | null>(null);

  // Add folder download hook
  const { 
    openFolderDownloadDialog, 
    FolderDownloadDialogComponent 
  } = useFolderDownload();

  // Add new state for folder selection
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState<{ id: string; name: string; type: 'file' | 'folder' } | null>(null);
  const [propertiesDialogOpen, setPropertiesDialogOpen] = useState(false);
  const [itemForProperties, setItemForProperties] = useState<PropertiesItem | null>(null);

  // Pagination state — reset to page 1 during render when browse identity changes
  const [page, setPage] = useState(1);
  const listingCursors=useRef<(string|undefined)[]>([undefined]);
  const [listingPage,setListingPage]=useState<{hasMore:boolean;nextCursor:string|null}>({hasMore:false,nextCursor:null});
  const [pageBrowseKey, setPageBrowseKey] = useState(browseKey);
  if (pageBrowseKey !== browseKey) {
    setPageBrowseKey(browseKey);
    setPage(1);
  }

  // State for blank-area context menu
  const [blankMenuPosition, setBlankMenuPosition] = useState<{ mouseX: number; mouseY: number } | null>(null);

  const loadGenerationRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentFolderContentsRef = useRef(currentFolderContents);
  const uiSelectedItemsRef = useRef(uiSelectedItems);
  const forceRefreshContentsRef = useRef<() => void>(() => {});
  const handlePasteFromClipboardRef = useRef<() => void>(() => {});
  const handleBatchOperationRef = useRef<(operation: string) => void>(() => {});
  const handleSelectAllRef = useRef<() => void>(() => {});
  const handleViewModeChangeRef = useRef<(event: React.MouseEvent<HTMLElement> | null, newViewMode: string) => void>(() => {});
  const viewOptionsRef = useRef(viewOptions);
  const searchInputRef = useRef<HTMLInputElement>(null);

  currentFolderContentsRef.current = currentFolderContents;
  uiSelectedItemsRef.current = uiSelectedItems;
  viewOptionsRef.current = viewOptions;

  // Sync searchText with URL param
  useEffect(() => {
    setSearchText(searchQuery || '');
    setPage(1);
  }, [searchQuery]);

  useEffect(()=>{listingCursors.current=[undefined];setPage(1);},[browseKey,searchQuery,sortBy,sortDirection,selectedFileType]);

  // Clear stale list when folder / virtual view / filter identity changes
  useEffect(() => {
    dispatch(clearFolderContents());
  }, [browseKey, dispatch]);

  // Handle upload dialog from URL parameter
  useEffect(() => {
    const uploadParam = searchParams.get('upload');
    if (!uploadParam) return;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('upload');
    const nextQuery = newSearchParams.toString();
    navigate(nextQuery ? `?${nextQuery}` : location.pathname, { replace: true });

    if (uploadParam === 'true' && effectiveUploadFolderId) {
      dispatch(openUploadDialog({ folderId: effectiveUploadFolderId }));
      return;
    }

    // upload=blocked, upload=true on root/virtual, or any blocked context
    enqueueSnackbar(ROOT_UPLOAD_ERROR, { variant: 'error' });
  }, [searchParams, navigate, dispatch, effectiveUploadFolderId, enqueueSnackbar, location.pathname]);

  // Define loadRecentActivity before it's used
  const loadRecentActivity = useCallback(async () => {
    try {
      setIsLoadingActivity(true);
      // Try to load activities, but fail gracefully if endpoint doesn't exist
      try {
        const response = await activityService.getActivities(1, 5);
        setRecentActivity(response.activities);
      } catch (error: any) {
        // If we get a 404, the endpoint doesn't exist (yet)
        if (error?.response?.status === 404) {

          setRecentActivity([]); // Set empty array to prevent repeated attempts
        } else {
          // For other errors, log and show notification

          enqueueSnackbar('Failed to load recent activity', { variant: 'error' });
        }
      }
    } finally {
      setIsLoadingActivity(false);
    }
  }, [enqueueSnackbar]);

  // Load folder / virtual-view contents with abort + generation guards
  const loadFolderContents = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const generation = ++loadGenerationRef.current;
    const { signal } = controller;

    const isStale = () => generation !== loadGenerationRef.current || signal.aborted;

    try {
      dispatch(fetchFolderContentsStart());

      // If view mode is active, load the dataset for that view in this same page.
      if (filesView) {
        try {
          if (filesView === 'recent') {
            const response = await api.get('/files/recent', { signal });
            if (isStale()) return;
            const files = (response.data?.files || response.data?.items || response.data || []).map((file: any) => ({
              ...file,
              type: 'file'
            }));
            dispatch(fetchFolderContentsSuccess({ folders: [], files }));
          } else if (filesView === 'favorites') {
            const response = await api.get('/folders/special/favorites', { signal });
            if (isStale()) return;
            const folders = (response.data?.folders || []).map((folder: any) => ({ ...folder, type: 'folder' }));
            const files = (response.data?.files || []).map((file: any) => ({ ...file, type: 'file' }));
            dispatch(fetchFolderContentsSuccess({ folders, files }));
          } else if (filesView === 'trash') {
            const [filesRes, foldersRes] = await Promise.all([
              api.get('/files/trash', { signal }),
              api.get('/folders/trash', { signal })
            ]);
            if (isStale()) return;
            const files = (filesRes.data?.files || filesRes.data?.items || filesRes.data || []).map((file: any) => ({
              ...file,
              type: 'file' as const
            }));
            const folders = (foldersRes.data?.folders || []).map((folder: any) => ({
              ...folder,
              type: 'folder' as const
            }));
            dispatch(fetchFolderContentsSuccess({ folders, files }));
          } else if (filesView === 'shared-with-me') {
            const response = await api.get('/shares/shared-with-me', { signal });
            if (isStale()) return;
            const resources = response.data?.sharedResources || [];
            const folders = resources
              .filter((share: any) => share.resourceType === 'folder')
              .map((share: any) => ({
                ...(share.resource || {}),
                id: share.resourceId,
                name: share.resource?.name || 'Shared Folder',
                type: 'folder'
              }));
            const files = resources
              .filter((share: any) => share.resourceType === 'file')
              .map((share: any) => ({
                ...(share.resource || {}),
                id: share.resourceId,
                name: share.resource?.name || 'Shared File',
                type: 'file'
              }));
            dispatch(fetchFolderContentsSuccess({ folders, files }));
          } else if (filesView === 'my-shared') {
            const response = await api.get('/shares/my-shared', { signal });
            if (isStale()) return;
            const resources = Array.from(new Map([...(response.data?.shareLinks || []), ...(response.data?.userShares || [])].map((share:any)=>[`${share.resourceType}:${share.resourceId}`,share])).values());
            const folders = resources
              .filter((share: any) => share.resourceType === 'folder')
              .map((share: any) => ({
                ...(share.resource || {}),
                id: share.resourceId,
                name: share.resource?.name || 'Shared Folder',
                type: 'folder'
              }));
            const files = resources
              .filter((share: any) => share.resourceType === 'file')
              .map((share: any) => ({
                ...(share.resource || {}),
                id: share.resourceId,
                name: share.resource?.name || 'Shared File',
                type: 'file'
              }));
            dispatch(fetchFolderContentsSuccess({ folders, files }));
          } else {
            const libraryTypeMap: Record<string, string> = {
              images: 'image',
              videos: 'video',
              documents: 'document',
              audio: 'audio',
              archives: 'archive',
              code: 'code'
            };
            const response = await api.get('/files', {
              params: {
                type: libraryTypeMap[filesView],
                scope: 'tenant',
                sortBy,
                sortDirection
              },
              signal
            });
            if (isStale()) return;
            const files = (response.data?.items || []).map((file: any) => ({ ...file, type: 'file' }));
            dispatch(fetchFolderContentsSuccess({ folders: [], files }));
          }

          if (isStale()) return;
          dispatch(fetchFolderPathSuccess([]));
          dispatch(setCurrentFolder(null));
          return;
        } catch (error) {
          if (isRequestCanceled(error) || isStale()) {
            if (generation === loadGenerationRef.current) {
              dispatch(fetchFolderContentsFailure(''));
            }
            return;
          }
          enqueueSnackbar('Error loading view data', { variant: 'error' });
          dispatch(fetchFolderContentsFailure('Failed to load view data'));
          return;
        }
      }

      // If a filter is active, load filtered files only (no folders)
      if (filterId) {
        try {
          const response = await api.get('/files', {
            params: {
              filter: filterId,
              sortBy: sortBy,
              sortDirection: sortDirection
            },
            signal
          });
          if (isStale()) return;

          const files = response.data.items || [];
          const fileItems = files.map((file: any) => ({ ...file, type: 'file' }));

          dispatch(fetchFolderContentsSuccess({ folders: [], files: fileItems }));
          dispatch(fetchFolderPathSuccess([]));
          dispatch(setCurrentFolder(null));
          return;
        } catch (error) {
          if (isRequestCanceled(error) || isStale()) {
            if (generation === loadGenerationRef.current) {
              dispatch(fetchFolderContentsFailure(''));
            }
            return;
          }
          enqueueSnackbar('Error loading filtered files', { variant: 'error' });
          dispatch(fetchFolderContentsFailure('Failed to load filtered files'));
          return;
        }
      }

      let actualFolderId: string;

      // Determine current folder (or root)
      if (folderId) {
        try {
          const folderData = await folderService.getFolder(folderId, { signal });
          if (isStale()) return;
          dispatch(setCurrentFolder(folderData));
          actualFolderId = folderId;
          // Build breadcrumbs only if this load is still current (avoids stale path overwrite)
          try {
            const path: { id: string; name: string; isLast: boolean }[] = [];
            let current: any = folderData;
            const seen = new Set<string>();
            while (current?.parentId && !seen.has(current.parentId)) {
              seen.add(current.parentId);
              const parent = await folderService.getFolder(current.parentId, { signal });
              if (isStale()) return;
              path.unshift({ ...parent, isLast: false });
              current = parent;
            }
            if (path.length > 0) {
              path[path.length - 1] = { ...path[path.length - 1], isLast: true };
            }
            if (!isStale()) {
              dispatch(fetchFolderPathSuccess(path as any));
            }
          } catch (pathErr) {
            if (isRequestCanceled(pathErr) || isStale()) return;
          }
        } catch (e) {
          if (isRequestCanceled(e) || isStale()) return;
          dispatch(setCurrentFolder(null));
          actualFolderId = folderId;
        }
      } else {
        dispatch(fetchFolderPathSuccess([]));
        try {
          const rootData = await folderService.getFolder('root', { signal });
          if (isStale()) return;
          dispatch(setCurrentFolder(rootData));
          setRootFolderId(rootData.id);
          actualFolderId = rootData.id;
        } catch (e) {
          if (isRequestCanceled(e) || isStale()) return;
          actualFolderId = 'root';
        }
      }

      const fullResponse = await folderService.getFolderContents(actualFolderId, {
        limit:50, cursor:listingCursors.current[page-1],fileType:selectedFileType,
        search: searchQuery || undefined,
        sortBy: sortBy,
        sortDirection: sortDirection,
        signal
      });
      if (isStale()) return;

      const items = fullResponse.items;
      setListingPage(fullResponse.pagination||{hasMore:false,nextCursor:null});

      const folderItems = (items as any[]).filter(item => item.type === 'folder') as Folder[];
      const fileItems = (items as any[]).filter(item => item.type === 'file') as FileItem[];
      dispatch(fetchFolderContentsSuccess({ folders: folderItems, files: fileItems }));
    } catch (error) {
      if (isRequestCanceled(error) || isStale()) {
        // If nothing newer started, clear stuck loading
        if (generation === loadGenerationRef.current) {
          dispatch(fetchFolderContentsFailure(''));
        }
        return;
      }
      dispatch(fetchFolderContentsFailure('Failed to load folder contents'));
      enqueueSnackbar('Error loading folder contents', { variant: 'error' });
    }
  }, [dispatch, folderId, filterId, filesView, searchQuery, sortBy, sortDirection, enqueueSnackbar, page, selectedFileType]);

  // Single load effect — runs once per intentional dependency change
  useEffect(() => {
    void loadFolderContents();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [loadFolderContents]);

  // Load recent activity on initial mount only
  useEffect(() => {
    void loadRecentActivity();
    // Intentionally mount-only; activity strip is independent of browse navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Server currently emits only connection/pong — no FILE_UPDATED/FOLDER_UPDATED.
  // Keep subscription hooks ready but do not refresh the listing until real change events exist.
  useEffect(() => {
    if (filesView) {
      return;
    }

    const noop = () => {};
    websocketService.subscribe(WS_EVENTS.FILE_UPDATED, noop);
    websocketService.subscribe(WS_EVENTS.FOLDER_UPDATED, noop);
    return () => {
      websocketService.unsubscribe(WS_EVENTS.FILE_UPDATED, noop);
      websocketService.unsubscribe(WS_EVENTS.FOLDER_UPDATED, noop);
    };
  }, [folderId, filesView]);

  // Handle file operations
  const handleFileOperation = async (operation: string, fileId: string, data?: any) => {
    try {
      switch (operation) {
        case 'preview': {
          const parentForPreview = folderId || currentFolder?.id || 'root';
          navigate(`/preview/${fileId}?folderId=${encodeURIComponent(parentForPreview)}`);
          break;
        }
        case 'download':
          enqueueSnackbar('Downloading file...', { variant: 'info' });
          await fileService.downloadFile(fileId);
          enqueueSnackbar('File downloaded successfully', { variant: 'success' });
          break;
        case 'share':
          // Open share dialog for this file
          const fileToShare = currentFolderContents?.files.find(file => file.id === fileId);
          if (fileToShare) {
            setShareItem({
              id: fileId,
              name: fileToShare.name || 'Unknown file',
              type: 'file'
            });
            setIsShareDialogOpen(true);
          }
          break;
        case 'rename':
          // Open rename dialog
          const fileToRename = currentFolderContents?.files.find(file => file.id === fileId);
          if (fileToRename) {
            setItemToRename({
              id: fileId,
              name: fileToRename.name || 'Unknown file',
              type: 'file'
            });
            setRenameDialogOpen(true);
          }
          break;
        case 'restore':
          enqueueSnackbar('Restoring file...', { variant: 'info' });
          await fileService.restoreFile(fileId);
          enqueueSnackbar('File restored', { variant: 'success' });
          loadFolderContents();
          break;
        case 'delete':
          if (filesView === 'trash') {
            if (!window.confirm('Permanently delete this file? This cannot be undone.')) break;
            enqueueSnackbar('Deleting file...', { variant: 'info' });
            await fileService.deleteFilePermanent(fileId);
            enqueueSnackbar('File permanently deleted', { variant: 'success' });
          } else {
            enqueueSnackbar('Deleting file...', { variant: 'info' });
            await fileService.deleteFile(fileId);
            enqueueSnackbar('File deleted successfully', { variant: 'success' });
          }
          loadFolderContents();
          break;
        case 'star':
          await api.put(`/folders/special/favorites/${fileId}`, {itemType:'file',isStarred:Boolean(data)});
          enqueueSnackbar(data ? 'Added to favorites' : 'Removed from favorites', { variant: 'success' });
          loadFolderContents();
          break;
        case 'copy': {
          const fileToCopy = currentFolderContents?.files.find(f => f.id === fileId);
          dispatch(copyToClipboard({ items: [{ id: fileId, type: 'file', name: fileToCopy?.name || 'Unknown file' }], sourceFolder: folderId || 'root' }));
          enqueueSnackbar('Copied to clipboard', { variant: 'success' });
          break;
        }
        case 'move': {
          const fileToMove = currentFolderContents?.files.find(f => f.id === fileId);
          dispatch(cutToClipboard({ items: [{ id: fileId, type: 'file', name: fileToMove?.name || 'Unknown file' }], sourceFolder: folderId || 'root' }));
          enqueueSnackbar('Cut to clipboard', { variant: 'success' });
          break;
        }
        case 'properties': {
          const fileForProps = currentFolderContents?.files.find(f => f.id === fileId);
          if (fileForProps) {
            setItemForProperties({
              id: fileId,
              name: fileForProps.name || 'Unknown file',
              type: 'file',
            });
            setPropertiesDialogOpen(true);
          }
          break;
        }
        default:

      }
    } catch (error) {

      enqueueSnackbar(`Failed to ${operation} file`, { variant: 'error' });
    }
  };

  // Handle folder operations
  const handleFolderOperation = async (operation: string, folderId: string, data?: any) => {

    switch (operation) {
      case 'open':
        if (filesView === 'trash') return;
        navigate(`/files/${folderId}`);
        break;
      case 'restore':
        enqueueSnackbar('Restoring folder...', { variant: 'info' });
        try {
          await folderService.restoreFolder(folderId);
          enqueueSnackbar('Folder restored', { variant: 'success' });
          loadFolderContents();
        } catch (e) {
          enqueueSnackbar('Failed to restore folder', { variant: 'error' });
        }
        break;
      case 'rename':
        const folder = currentFolderContents?.folders.find(f => f.id === folderId);
        if (folder) {
          setItemToRename({
            id: folderId,
            name: folder.name || 'Unknown folder',
            type: 'folder'
          });
          setRenameDialogOpen(true);
        }
        break;
      case 'move': {
        const folderToMove = currentFolderContents?.folders.find(f => f.id === folderId);
        dispatch(cutToClipboard({ items: [{ id: folderId, type: 'folder', name: folderToMove?.name || 'Unknown folder' }], sourceFolder: currentFolder?.id || 'root' }));
        enqueueSnackbar('Cut to clipboard', { variant: 'success' });
        break;
      }
      case 'copy': {
        const folderToCopy = currentFolderContents?.folders.find(f => f.id === folderId);
        dispatch(copyToClipboard({ items: [{ id: folderId, type: 'folder', name: folderToCopy?.name || 'Unknown folder' }], sourceFolder: currentFolder?.id || 'root' }));
        enqueueSnackbar('Copied to clipboard', { variant: 'success' });
        break;
      }
      case 'properties': {
        const folderForProps = currentFolderContents?.folders.find(f => f.id === folderId);
        if (folderForProps) {
          setItemForProperties({
            id: folderId,
            name: folderForProps.name || 'Unknown folder',
            type: 'folder',
          });
          setPropertiesDialogOpen(true);
        }
        break;
      }
      case 'share':
        // Open share dialog for this folder
        const folderToShare = currentFolderContents?.folders.find(f => f.id === folderId);
        if (folderToShare) {
          setShareItem({
            id: folderId,
            name: folderToShare.name || 'Unknown folder',
            type: 'folder'
          });
          setIsShareDialogOpen(true);
        }
        break;
      case 'star':
        // Use the fileService to toggle favorite status
        api.put(`/folders/special/favorites/${folderId}`, {itemType:'folder',isStarred:Boolean(data)})
          .then(() => {
            enqueueSnackbar(data ? 'Added to favorites' : 'Removed from favorites', { variant: 'success' });
            loadFolderContents();
          })
          .catch(error => {

            enqueueSnackbar('Failed to update favorite status', { variant: 'error' });
          });
        break;
      case 'delete': {
        const folderItem = currentFolderContents?.folders.find(f => f.id === folderId);
        const folderName = folderItem?.name || 'this folder';
        if (filesView === 'trash') {
          dispatch(openConfirmDialog({
            title: 'Delete permanently',
            message: `Permanently delete "${folderName}" and its trashed contents? This cannot be undone.`,
            confirmText: 'Delete forever',
            cancelText: 'Cancel',
            severity: 'error',
            onConfirm: async () => {
              try {
                await folderService.deleteFolder(folderId, true, true);
                enqueueSnackbar('Folder permanently deleted', { variant: 'success' });
                loadFolderContents();
              } catch (error) {
                enqueueSnackbar('Failed to delete folder', { variant: 'error' });
              }
            }
          }));
        } else {
          dispatch(openConfirmDialog({
            title: 'Confirm Delete',
            message: `Are you sure you want to delete "${folderName}"?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            severity: 'warning',
            onConfirm: async () => {
              enqueueSnackbar('Deleting folder...', { variant: 'info' });
              try {
                await folderService.deleteFolder(folderId, true);
                enqueueSnackbar('Folder deleted successfully', { variant: 'success' });
                loadFolderContents();
              } catch (error) {
                enqueueSnackbar('Failed to delete folder', { variant: 'error' });
              }
            }
          }));
        }
        break;
      }
      case 'download':
        const folderToDownload = currentFolderContents?.folders.find(f => f.id === folderId);
        if (folderToDownload) {
          openFolderDownloadDialog(folderToDownload.id, folderToDownload.name);
        }
        break;
      default:
        break;
    }
  };

  // Handle paste operation from clipboard
  const pasteBusyRef=useRef(false);
  const handlePasteFromClipboard = async () => {
    if(pasteBusyRef.current)return;
    const {items,actionType}=clipboard;
    if(!items.length||!actionType){enqueueSnackbar('Nothing to paste',{variant:'info'});return;}
    pasteBusyRef.current=true;
    try{
      dispatch(fetchFolderContentsStart());
      const operation=actionType==='cut'?'move':'copy';
      const response=await api.post(`/folders/${operation}`,{items:items.map(({id,type})=>({id,type})),destinationId:folderId||'root'});
      const results=response.data?.results;
      if(!results || !Array.isArray(results.success)||!Array.isArray(results.failed))throw new Error('Transfer result unavailable; refresh the folder to reconcile');
      if(actionType==='cut'){
        const successful=new Set(results.success.map((row:any)=>row.id));
        const remaining=items.filter(item=>!successful.has(item.id));
        if(remaining.length)dispatch(cutToClipboard({items:remaining,sourceFolder:clipboard.sourceFolder||'root'}));else dispatch(clearClipboard());
      }
      if(results.success.length)enqueueSnackbar(`${results.success.length} items ${operation==='copy'?'copied':'moved'}`,{variant:'success'});
      if(results.failed.length)enqueueSnackbar(`${results.failed.length} items failed: ${results.failed[0].reason}`,{variant:'error'});
    }catch(error:any){
      enqueueSnackbar(error.response?.data?.message||'Transfer could not be confirmed. Refresh before retrying.',{variant:'error'});
    }finally{pasteBusyRef.current=false;await loadFolderContents();}
  };

  // Handle batch operations
  const handleBatchOperation = async (operation: string) => {
    const { files, folders } = uiSelectedItems;

    switch (operation) {
      case 'delete':
        try {
          // Validate that we have items to delete
          if (files.length === 0 && folders.length === 0) {
            enqueueSnackbar('No items selected for deletion', { variant: 'warning' });
            return;
          }

          if (filesView === 'trash') {
            const total = files.length + folders.length;
            if (!window.confirm(`Permanently delete ${total} item(s)? This cannot be undone.`)) {
              return;
            }
            dispatch(fetchFolderContentsStart());
            let deleteSuccess = 0;
            const deleteErrors: string[] = [];
            for (const fileId of files) {
              try {
                await fileService.deleteFilePermanent(fileId);
                deleteSuccess++;
              } catch (error: any) {
                console.error(`Failed to permanently delete file ${fileId}:`, error);
                deleteErrors.push(`File ${fileId}: ${error.message || 'Unknown error'}`);
              }
            }
            for (const delFolderId of folders) {
              try {
                await folderService.deleteFolder(delFolderId, true, true);
                deleteSuccess++;
              } catch (error: any) {
                console.error(`Failed to permanently delete folder ${delFolderId}:`, error);
                deleteErrors.push(`Folder ${delFolderId}: ${error.message || 'Unknown error'}`);
              }
            }
            await loadFolderContents();
            if (deleteSuccess > 0) {
              enqueueSnackbar(`Permanently deleted ${deleteSuccess} of ${total} items`, { variant: 'success' });
            }
            if (deleteErrors.length > 0) {
              console.error('Delete errors:', deleteErrors);
              enqueueSnackbar(`Failed to delete ${deleteErrors.length} items`, { variant: 'error' });
            }
            dispatch(clearUiSelection());
            break;
          }

          dispatch(fetchFolderContentsStart());
          
          let deleteSuccess = 0;
          const deleteErrors: string[] = [];
          
          // Delete files - only the ones in the selection
          for (const fileId of files) {
            try {
              await fileService.deleteFile(fileId);
              deleteSuccess++;
            } catch (error: any) {
              console.error(`Failed to delete file ${fileId}:`, error);
              deleteErrors.push(`File ${fileId}: ${error.message || 'Unknown error'}`);
            }
          }
          
          // Delete folders - only the ones in the selection
          for (const folderId of folders) {
            try {
              // Delete folder recursively
              await folderService.deleteFolder(folderId, true);
              deleteSuccess++;
            } catch (error: any) {
              console.error(`Failed to delete folder ${folderId}:`, error);
              deleteErrors.push(`Folder ${folderId}: ${error.message || 'Unknown error'}`);
            }
          }
          
          await loadFolderContents();
          
          // Show success message
          const totalAttempted = files.length + folders.length;
          if (deleteSuccess > 0) {
            enqueueSnackbar(`Successfully deleted ${deleteSuccess} of ${totalAttempted} items`, { variant: 'success' });
          }
          if (deleteErrors.length > 0) {
            console.error('Delete errors:', deleteErrors);
            enqueueSnackbar(`Failed to delete ${deleteErrors.length} items`, { variant: 'error' });
          }
          
          // Clear UI selection
          dispatch(clearUiSelection());
          
        } catch (error: any) {
          console.error('Batch delete error:', error);
          dispatch(fetchFolderContentsFailure('Failed to delete items'));
          enqueueSnackbar('Failed to delete items', { variant: 'error' });
        }
        break;
      
      case 'copy':
        try {
          if (!currentFolderContents) return;
          
          // Prepare items for clipboard
          const clipboardItems: ClipboardItem[] = [
            ...files.map(fileId => {
              const file = currentFolderContents.files.find(f => f.id === fileId);
              return {
                id: fileId,
                type: 'file' as const,
                name: file?.name || 'Unknown file'
              };
            }),
            ...folders.map(folderId => {
              const folder = currentFolderContents.folders.find(f => f.id === folderId);
              return {
                id: folderId,
                type: 'folder' as const,
                name: folder?.name || 'Unknown folder'
              };
            })
          ];
          
          // Copy to clipboard with current folder as source
          dispatch(copyToClipboard({ 
            items: clipboardItems, 
            sourceFolder: folderId || 'root' 
          }));
          
          enqueueSnackbar(`Copied ${clipboardItems.length} items to clipboard`, { variant: 'success' });
        } catch (error) {

          enqueueSnackbar('Failed to copy items', { variant: 'error' });
        }
        break;
      
      case 'cut':
        try {
          if (!currentFolderContents) return;
          
          // Prepare items for clipboard
          const clipboardItems: ClipboardItem[] = [
            ...files.map(fileId => {
              const file = currentFolderContents.files.find(f => f.id === fileId);
              return {
                id: fileId,
                type: 'file' as const,
                name: file?.name || 'Unknown file'
              };
            }),
            ...folders.map(folderId => {
              const folder = currentFolderContents.folders.find(f => f.id === folderId);
              return {
                id: folderId,
                type: 'folder' as const,
                name: folder?.name || 'Unknown folder'
              };
            })
          ];
          
          // Cut to clipboard with current folder as source
          dispatch(cutToClipboard({ 
            items: clipboardItems, 
            sourceFolder: folderId || 'root' 
          }));
          
          enqueueSnackbar(`Cut ${clipboardItems.length} items to clipboard`, { variant: 'success' });
        } catch (error) {

          enqueueSnackbar('Failed to cut items', { variant: 'error' });
        }
        break;
      
      case 'download':
        try {
          if (!currentFolderContents) return;
          
          // Download files
          for (const fileId of files) {
            await fileService.downloadFile(fileId);
          }
          
          // Download folders
          for (const folderId of folders) {
            const folder = currentFolderContents.folders.find(f => f.id === folderId);
            if (folder) {
              openFolderDownloadDialog(folder.id, folder.name);
            }
          }
          
          enqueueSnackbar('Download started', { variant: 'success' });
        } catch (error) {

          enqueueSnackbar('Failed to download items', { variant: 'error' });
        }
        break;
      
      case 'share':
        try {
          if (!currentFolderContents) return;
          
          // Share files
          for (const fileId of files) {
            const file = currentFolderContents.files.find(f => f.id === fileId);
            if (file) {
              setShareItem({
                id: fileId,
                name: file.name || 'Unknown file',
                type: 'file'
              });
              setIsShareDialogOpen(true);
            }
          }
          
          // Share folders
          for (const folderId of folders) {
            const folder = currentFolderContents.folders.find(f => f.id === folderId);
            if (folder) {
              setShareItem({
                id: folderId,
                name: folder.name || 'Unknown folder',
                type: 'folder'
              });
              setIsShareDialogOpen(true);
            }
          }
        } catch (error) {

          enqueueSnackbar('Failed to share items', { variant: 'error' });
        }
        break;
      
      case 'move':
        try {
          if (!currentFolderContents) return;
          
          // Validate that we have items to move
          if (files.length === 0 && folders.length === 0) {
            enqueueSnackbar('No items selected for move', { variant: 'warning' });
            return;
          }

          // Build items array for clipboard
          const itemsToMove = [
            ...files.map(fileId => {
              const file = currentFolderContents.files.find(f => f.id === fileId);
              return {
                id: fileId,
                type: 'file' as const,
                name: file?.name || 'Unknown file'
              };
            }),
            ...folders.map(folderId => {
              const folder = currentFolderContents.folders.find(f => f.id === folderId);
              return {
                id: folderId,
                type: 'folder' as const,
                name: folder?.name || 'Unknown folder'
              };
            })
          ];
          
          // Use clipboard cut mechanism for move
          dispatch(cutToClipboard({ 
            items: itemsToMove, 
            sourceFolder: folderId || 'root' 
          }));
          
          enqueueSnackbar(`${itemsToMove.length} items cut to clipboard. Navigate to destination and paste.`, { 
            variant: 'info',
            autoHideDuration: 5000
          });
          
          // Clear selection after cut
          dispatch(clearUiSelection());
        } catch (error: any) {
          console.error('Move operation error:', error);
          enqueueSnackbar('Failed to prepare items for move', { variant: 'error' });
        }
        break;
      
      case 'star':
        try {
          if (!currentFolderContents) return;
          
          // Toggle star status for files
          for (const fileId of files) {
            const file = currentFolderContents.files.find(f => f.id === fileId);
            if (file) {
              await api.put(`/folders/special/favorites/${fileId}`, {itemType:'file',isStarred:!file.isStarred});
            }
          }
          
          // Toggle star status for folders
          for (const folderId of folders) {
            const folder = currentFolderContents.folders.find(f => f.id === folderId);
            if (folder) {
              await api.put(`/folders/special/favorites/${folderId}`, {itemType:'folder',isStarred:!folder.isStarred});
            }
          }
          
          enqueueSnackbar('Updated favorite status', { variant: 'success' });
          loadFolderContents();
        } catch (error) {

          enqueueSnackbar('Failed to update favorite status', { variant: 'error' });
        }
        break;
      
      default:

        break;
    }
  };

  // Update the drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragTarget(null);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      
      const item: DraggedItem = JSON.parse(data);
      if (item.id) {
        const targetFolderId = folderId;
        if (item.type === 'file') {
          await fileService.moveFile(item.id, targetFolderId);
        } else if (item.type === 'folder') {
          await folderService.moveFolder(item.id, targetFolderId);
        }
        loadFolderContents();
        enqueueSnackbar('Item moved successfully', { variant: 'success' });
      }
    } catch (error) {

      enqueueSnackbar('Failed to move item', { variant: 'error' });
    }
  }, [folderId, loadFolderContents, enqueueSnackbar]);

  // Handle sort menu
  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortMenuAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortMenuAnchorEl(null);
  };

  const handleSortByChange = (sortBy: SortBy) => {
    dispatch(setSortBy(sortBy));
    handleSortClose();
  };

  const handleSortDirectionToggle = () => {
    dispatch(setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'));
  };

  // Toggle view mode (grid/list)
  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement> | null,
    newViewMode: string,
  ) => {
    if (newViewMode !== null) {
      setViewOptions(newViewMode);
      dispatch(setViewMode(newViewMode as 'grid' | 'list' | 'compact'));
    }
  };

  // New function to handle selection mode toggle
  const handleToggleSelectionMode = () => {
    dispatch(toggleSelectionMode());
  };

  // New function to handle select all
  const handleSelectAll = () => {
    if (!currentFolderContents) return;
    
    const { files, folders } = currentFolderContents;
    
    const fileIds = files.map(file => file.id);
    const folderIds = folders.map(folder => folder.id);
    
    dispatch(selectAllItems({ fileIds, folderIds }));
  };

  // New function to handle file type filter
  const handleFileTypeChange = (type: FileType) => {
    listingCursors.current=[undefined];setPage(1);
    setSelectedFileType(type);
  };

  // New function to handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleSearch = () => {
    // Maintain folder context when searching
    const basePath = `/files${folderId ? '/' + folderId : ''}`;
    const baseParams = new URLSearchParams();
    if (filesView) {
      baseParams.set('view', filesView);
    }
    if (searchText) {
      baseParams.set('search', searchText);
      navigate(`${basePath}?${baseParams.toString()}`);
    } else {
      const query = baseParams.toString();
      navigate(query ? `${basePath}?${query}` : basePath);
    }
  };

  // Enter key in search field triggers search
  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Sort files and folders
  const sortItems = (items: any[] | null | undefined, sortBy: SortBy, sortDirection: SortDirection) => {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    return [...items].sort((a, b) => {
      if (!a || !b) return 0;
      
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'date':
          comparison = new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime();
          break;
        case 'size':
          // Only applicable for files
          if ('size' in a && 'size' in b) {
            comparison = (a.size || 0) - (b.size || 0);
          }
          break;
        case 'type':
          if ('mimeType' in a && 'mimeType' in b) {
            comparison = (a.mimeType || '').localeCompare(b.mimeType || '');
          }
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Add handler for advanced search
  const handleAdvancedSearch = (query: string, filters: any) => {

    // Implementation will depend on your API
    // Example: dispatch(searchFiles({ query, filters }) as any);
  };

  // Filter items by type
  const filterItemsByType = (folders: any[], files: any[]): [any[], any[]] => {
    if (selectedFileType === 'all') {
      return [folders, files];
    }

    if (selectedFileType === 'folders') {
      return [folders, []];
    }

    const filteredFiles = files.filter(file => {
      const mimeType = file.mimeType || file.mimetype || '';
      
      switch (selectedFileType) {
        case 'documents':
          return mimeType.includes('document') || mimeType.includes('word') || 
                 mimeType.includes('text') || mimeType.includes('sheet') || 
                 mimeType.includes('excel') || mimeType.includes('presentation');
        case 'images':
          return mimeType.includes('image');
        case 'videos':
          return mimeType.includes('video');
        case 'audio':
          return mimeType.includes('audio');
        case 'pdfs':
          return mimeType.includes('pdf');
        case 'others':
          return !mimeType.includes('document') && !mimeType.includes('word') && 
                 !mimeType.includes('image') && !mimeType.includes('video') && 
                 !mimeType.includes('audio') && !mimeType.includes('pdf');
        default:
          return true;
      }
    });
    
    return [[], filteredFiles];
  };

  // Handle folder selection
  const handleFolderSelect = (selectedFolderId: string) => {
    try {
      if (filesView === 'trash') {
        return;
      }
      // Clear any existing search when navigating to a folder

      // Clear any selected items
      dispatch(clearUiSelection());
      
      // Clear any search text
      setSearchText('');
      
      // Navigate to the folder
      navigate(`/files/${selectedFolderId}`);
    } catch (error) {

      enqueueSnackbar('Failed to navigate to folder', { variant: 'error' });
    }
  };

  const handleItemSelection = (itemId: string, isSelected: boolean, type: 'file' | 'folder') => {
    // Use the uiSlice toggleSelectItem action instead of folderSlice actions
    dispatch(toggleSelectItem({ itemId, itemType: type }));
    
    // Enable selection mode if not already enabled
    if (!isSelectionMode && isSelected) {
      dispatch(toggleSelectionMode());
    }
  };

  // Handle filters expansion
  const handleFilterButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setFiltersAnchorEl(event.currentTarget);
  };

  const handleFiltersClose = () => {
    setFiltersAnchorEl(null);
  };

  const toggleFilterExpanded = () => {
    setFilterExpanded(!filterExpanded);
  };

  const handleShowDeletedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowDeletedFiles(event.target.checked);
    // Then fetch data with the new filter
    // loadFolderContents with appropriate parameters
  };

  const handleShowSharedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowSharedFiles(event.target.checked);
    // Then fetch data with the new filter
    // loadFolderContents with appropriate parameters
  };

  const handleDateRangeChange = (newDateRange: { from: Date | null; to: Date | null }) => {
    setDateRangeFilter(newDateRange);
    // Then fetch data with the new date range
    // loadFolderContents with appropriate parameters
  };

  // Inside component, add a force refresh method
  const forceRefreshContents = useCallback(() => {
    void loadFolderContents();
  }, [loadFolderContents]);

  forceRefreshContentsRef.current = forceRefreshContents;
  handlePasteFromClipboardRef.current = () => {
    void handlePasteFromClipboard();
  };
  handleBatchOperationRef.current = (operation: string) => {
    void handleBatchOperation(operation);
  };
  handleSelectAllRef.current = handleSelectAll;
  handleViewModeChangeRef.current = handleViewModeChange;

  const handleEmptyTrash = async () => {
    const hasItems = (currentFolderContents?.files?.length || 0) + (currentFolderContents?.folders?.length || 0) > 0;
    if (!hasItems) {
      enqueueSnackbar('Trash is already empty', { variant: 'info' });
      return;
    }

    const confirmEmpty = window.confirm('This will permanently delete all items in trash. Continue?');
    if (!confirmEmpty) {
      return;
    }

    try {
      await api.post('/files/trash/empty');
      enqueueSnackbar('Trash emptied successfully', { variant: 'success' });
      loadFolderContents();
    } catch (error) {
      enqueueSnackbar('Failed to empty trash', { variant: 'error' });
    }
  };

  // Render breadcrumbs component
  const renderBreadcrumbs = () => (
    <Box 
      sx={{ 
        py: 1.5, 
        mb: 2, 
        display: 'flex', 
        alignItems: 'center' 
      }}
    >
      {!isSpecialView && <Box sx={{display:'flex',gap:1,mr:2}}>
        <Button disabled={page===1||isLoading} onClick={()=>setPage(value=>value-1)}>Previous files</Button>
        <Typography sx={{alignSelf:'center'}}>Page {page}</Typography>
        <Button disabled={!listingPage.hasMore||isLoading} onClick={()=>{listingCursors.current[page]=listingPage.nextCursor||undefined;setPage(value=>value+1);}}>Next files</Button>
      </Box>}
      {/* Show breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        separator="›"
        sx={{ 
          '& .MuiBreadcrumbs-separator': { 
            mx: 1, 
            color: 'text.secondary' 
          } 
        }}
      >
        {/* Root folder */}
        <Link
          component="button"
          variant="body1"
          color={!folderId ? 'primary' : 'inherit'}
          onClick={() => navigate('/files')}
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontWeight: !folderId ? 'medium' : 'normal',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
          Home
        </Link>
        
        {/* Folder path (ancestor folders) */}
        {breadcrumbs.map((crumb, index) => {
          const isLastCrumb = index === breadcrumbs.length - 1;
          
          return (
            <Link
              key={crumb.id}
              component="button"
              variant="body1"
              color={isLastCrumb ? 'primary' : 'inherit'}
              onClick={() => navigate(`/files/${crumb.id}`)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: isLastCrumb ? 'medium' : 'normal',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {isLastCrumb ? (
                <FolderOutlinedIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
              ) : (
                <FolderOutlinedIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              )}
              {crumb.name}
            </Link>
          );
        })}
        
        {/* Current folder */}
        {currentFolder && (
          <Typography variant="body1" color="primary" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <FolderOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />{currentFolder.name}
          </Typography>
        )}
        
        {/* Show current filter if applicable */}
        {(filterId || filesView) && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterListIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
            <Typography variant="body1" color="primary" fontWeight="medium">
              {filesView ? VIEW_TITLES[filesView] : 'Filtered View'}
            </Typography>
          </Box>
        )}
      </Breadcrumbs>
    </Box>
  );

  // Render the enhanced header with action buttons
  const renderHeader = () => (
    <Paper 
      elevation={0}
      sx={{
        width: '100%',
        mb: 2, 
        p: { xs: 1.5, sm: 2, md: 2 },
        backgroundColor: 'background.paper',
        borderRadius: 0,
        boxShadow: 1,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 2 } 
      }}>
        <Box sx={{ flex: 1, minWidth: 0, mb: { xs: 1, sm: 0 } }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {folderId ? (
              currentFolder ? (
                currentFolder.name
              ) : (
                <Skeleton variant="text" width={150} />
              )
            ) : (
              filesView ? VIEW_TITLES[filesView] : 'My Files'
            )}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 0.5,
            maxWidth: '100%',
            overflowX: 'auto',
            pr: 2
          }}>
            {renderBreadcrumbs()}
          </Box>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          width: { xs: '100%', sm: 'auto' },
          flexShrink: 0
        }}>
          {filesView === 'trash' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlinedIcon />}
              onClick={handleEmptyTrash}
              size="small"
            >
              Empty Trash
            </Button>
          )}
          {filesView !== 'trash' && (
            <>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CreateNewFolderIcon />}
            onClick={() => setIsNewFolderDialogOpen(true)}
            size="small"
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            New Folder
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileUploadIcon />}
            onClick={requestUpload}
            size="small"
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Upload
          </Button>
            </>
          )}
          <IconButton
            color="primary"
            onClick={handleToggleSelectionMode}
            size="small"
            sx={{ 
              bgcolor: isSelectionMode ? 'primary.light' : 'transparent',
              color: isSelectionMode ? 'primary.contrastText' : 'primary.main',
              '&:hover': {
                bgcolor: isSelectionMode ? 'primary.main' : 'action.hover'
              }
            }}
          >
            <SelectAllIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: 'center', 
        gap: 1
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          width: { xs: '100%', sm: '50%' },
          maxWidth: '100%'
        }}>
          <TextField
            placeholder="Search files and folders..."
            inputRef={searchInputRef}
            value={searchText}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            sx={{ flexGrow: 1 }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton size="small" onClick={handleSearch} sx={{ p: 0.5 }}>
                    <SearchIcon color="action" />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      setSearchText('');
                      const basePath = '/files' + (folderId ? '/' + folderId : '');
                      const params = new URLSearchParams();
                      if (filesView) {
                        params.set('view', filesView);
                      }
                      const query = params.toString();
                      navigate(query ? `${basePath}?${query}` : basePath);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Tooltip title="Filter files">
            <IconButton
              onClick={handleFilterButtonClick}
              color={filtersAnchorEl ? 'primary' : 'default'}
              size="small"
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={filtersAnchorEl}
            open={Boolean(filtersAnchorEl)}
            onClose={handleFiltersClose}
            PaperProps={{
              sx: {
                width: 300,
                p: 2
              }
            }}
          >
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Filter Files
            </Typography>
            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup
                value={selectedFileType}
                exclusive
                onChange={(e, newValue) => handleFileTypeChange(newValue || 'all')}
                size="small"
                fullWidth
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="folders">Folders</ToggleButton>
                <ToggleButton value="documents">Docs</ToggleButton>
                <ToggleButton value="images">Images</ToggleButton>
                <ToggleButton value="videos">Videos</ToggleButton><ToggleButton value="audio">Audio</ToggleButton><ToggleButton value="pdfs">PDFs</ToggleButton><ToggleButton value="others">Other</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Button 
              variant="contained" 
              size="small"
              fullWidth
              onClick={handleFiltersClose}
              sx={{ mt: 2 }}
            >
              Apply
            </Button>
          </Menu>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-end' },
          mt: { xs: 1, sm: 0 }
        }}>
          <Button
            startIcon={<SortOutlinedIcon />}
            onClick={handleSortClick}
            size="small"
            variant="outlined"
            sx={{ display: { xs: 'none', sm: 'flex' } }}
            endIcon={sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          >
            Sort by: {sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}
          </Button>
          <IconButton
            onClick={handleSortClick}
            size="small"
            sx={{ display: { xs: 'flex', sm: 'none' } }}
          >
            <SortOutlinedIcon />
          </IconButton>
          <Menu
            anchorEl={sortMenuAnchorEl}
            open={Boolean(sortMenuAnchorEl)}
            onClose={handleSortClose}
          >
            <MenuItem onClick={() => handleSortByChange('name')} selected={sortBy === 'name'}>
              <ListItemIcon>
                <SortByAlphaIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Name</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleSortByChange('date')} selected={sortBy === 'date'}>
              <ListItemIcon>
                <HistoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Date</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleSortByChange('size')} selected={sortBy === 'size'}>
              <ListItemIcon>
                <OtherFilesOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Size</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleSortByChange('type')} selected={sortBy === 'type'}>
              <ListItemIcon>
                <FileOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Type</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleSortDirectionToggle}>
              <ListItemIcon>
                {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{sortDirection === 'asc' ? 'Ascending' : 'Descending'}</ListItemText>
            </MenuItem>
          </Menu>

          <ToggleButtonGroup
            value={viewOptions}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ListViewIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="compact" aria-label="compact view">
              <CompactViewIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Group by type">
            <IconButton
              onClick={() => setGroupByType(!groupByType)}
              size="small"
              color={groupByType ? 'primary' : 'default'}
              sx={{ ml: 1 }}
            >
              <GroupWorkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {filesView === 'trash' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Restore items to bring them back to your drive, or delete them permanently. Empty Trash removes all of your trashed files and folders.
        </Alert>
      )}
    </Paper>
  );

  // Enhanced renderContent function 
  const renderContent = () => {
    const listVariant = filesView === 'trash' ? 'trash' : 'default';
    const { folders = [], files = [] } = currentFolderContents || {};
    // Apply search filtering on names
    let visibleFolders = folders;
    let visibleFiles = files;
    if (searchText) {
      const lower = searchText.toLowerCase();
      visibleFolders = folders.filter(folder => folder.name.toLowerCase().includes(lower));
      visibleFiles = files.filter(file => file.name.toLowerCase().includes(lower));
    }
    const [filteredFolders, filteredFiles] = filterItemsByType(visibleFolders, visibleFiles);
    
    const hasItems = filteredFolders.length > 0 || filteredFiles.length > 0;
    
    if (isLoading) {
      return (
        <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
            {Array.from(new Array(6)).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Skeleton variant="rounded" height={200} animation="wave" />
            </Grid>
          ))}
        </Grid>
        </Box>
      );
    }
    
    if (!hasItems) {
      return (
        <EmptyState 
          icon={<FolderOutlinedIcon sx={{ fontSize: 64 }} />}
          title={filesView === 'trash' ? 'Trash is empty' : 'This folder is empty'}
          description={
            searchText
              ? `No items matching "${searchText}"`
              : filesView === 'trash'
                ? 'Deleted files and folders appear here.'
                : filesView
                  ? 'No items in this view yet.'
                  : !canUploadHere
                    ? 'Open a folder to upload files, or create a new folder to get started.'
                    : 'Upload files or create a new folder to get started'
          }
          type={filesView === 'trash' ? 'trash' : 'files'}
        />
      );
    }

    if (groupByType) {
      const docFiles = filteredFiles.filter(file => { const mime = file.mimeType || ''; return mime.includes('document') || mime.includes('word') || mime.includes('text') || mime.includes('sheet') || mime.includes('excel') || mime.includes('presentation'); });
      const imageFiles = filteredFiles.filter(file => (file.mimeType || '').includes('image'));
      const videoFiles = filteredFiles.filter(file => (file.mimeType || '').includes('video'));
      const audioFiles = filteredFiles.filter(file => (file.mimeType || '').includes('audio'));
      const pdfFiles = filteredFiles.filter(file => (file.mimeType || '').includes('pdf'));
      const otherFiles = filteredFiles.filter(file => { return !docFiles.includes(file) && !imageFiles.includes(file) && !videoFiles.includes(file) && !audioFiles.includes(file) && !pdfFiles.includes(file); });
      const groups = [
        { title: 'Folders', folders: filteredFolders, files: [] },
        { title: 'Documents', folders: [], files: docFiles },
        { title: 'Images', folders: [], files: imageFiles },
        { title: 'Videos', folders: [], files: videoFiles },
        { title: 'Audio', folders: [], files: audioFiles },
        { title: 'PDFs', folders: [], files: pdfFiles },
        { title: 'Others', folders: [], files: otherFiles },
      ];
      return (
        <Box sx={{ mt: 2 }}>
          {viewMode === 'list' ? (
            <>
              {groups.map(group => (group.folders.length + group.files.length > 0) && (
                <Box key={group.title} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{group.title}</Typography>
                  <List>
                    {group.folders.map(folder => (
                      <FolderListItem key={folder.id} folder={folder} layout="list" listVariant={listVariant} onClick={() => handleFolderSelect(folder.id)} onOperation={handleFolderOperation} isSelected={uiSelectedItems.folders.includes(folder.id)} onSelectionChange={selected => handleItemSelection(folder.id, selected, 'folder')} />
                    ))}
                    {group.files.map(file => (
                      <FileListItem key={file.id} file={file} layout="list" listVariant={listVariant} onClick={() => handleFileOperation('preview', file.id)} onOperation={handleFileOperation} isSelected={uiSelectedItems.files.includes(file.id)} onSelectionChange={selected => handleItemSelection(file.id, selected, 'file')} />
                    ))}
                  </List>
                </Box>
              ))}
            </>
          ) : (
            <>
              {groups.map(group => (group.folders.length + group.files.length > 0) && (
                <Box key={group.title} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{group.title}</Typography>
                  <Grid container spacing={2}>
                    {group.folders.map(folder => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={folder.id}>
                        <FolderCard folder={folder} listVariant={listVariant} onClick={() => handleFolderSelect(folder.id)} onOperation={handleFolderOperation} isSelected={uiSelectedItems.folders.includes(folder.id)} onSelectionChange={selected => handleItemSelection(folder.id, selected, 'folder')} />
                      </Grid>
                    ))}
                    {group.files.map(file => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                        <FileCard file={file} listVariant={listVariant} onClick={() => handleFileOperation('preview', file.id)} onOperation={handleFileOperation} isSelected={uiSelectedItems.files.includes(file.id)} onSelectionChange={selected => handleItemSelection(file.id, selected, 'file')} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        {viewMode === 'list' ? (
          <Paper elevation={0} variant="outlined" sx={{ borderRadius: theme.shape.borderRadius }}>
            <TableContainer>
              <Table aria-label="files and folders" size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      {isSelectionMode && (
                        <Checkbox 
                          onChange={handleSelectAll}
                          indeterminate={
                            (uiSelectedItems.files.length > 0 || uiSelectedItems.folders.length > 0) && 
                            uiSelectedItems.files.length + uiSelectedItems.folders.length < filteredFiles.length + filteredFolders.length
                          }
                          checked={
                            filteredFiles.length + filteredFolders.length > 0 &&
                            uiSelectedItems.files.length + uiSelectedItems.folders.length === filteredFiles.length + filteredFolders.length
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Modified</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
            {filteredFolders.map((folder) => (
                    <FolderListItem
                      key={folder.id}
                  folder={folder}
                  layout="table"
                  isSelectionMode={isSelectionMode}
                  listVariant={listVariant}
                  onClick={() => handleFolderSelect(folder.id)}
                  onOperation={handleFolderOperation}
                      isSelected={uiSelectedItems.folders.includes(folder.id)}
                      onSelectionChange={(selected) => 
                        handleItemSelection(folder.id, selected, 'folder')
                      }
                    />
            ))}
            {filteredFiles.map((file) => (
                    <FileListItem
                      key={file.id}
                  file={file}
                  layout="table"
                  isSelectionMode={isSelectionMode}
                  listVariant={listVariant}
                  onClick={() => handleFileOperation('preview', file.id)}
                  onOperation={handleFileOperation}
                      isSelected={uiSelectedItems.files.includes(file.id)}
                      onSelectionChange={(selected) => 
                        handleItemSelection(file.id, selected, 'file')
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {filteredFolders.map((folder) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={folder.id}>
                <FolderCard
                folder={folder}
                listVariant={listVariant}
                onClick={() => handleFolderSelect(folder.id)}
                onOperation={handleFolderOperation}
                  isSelected={uiSelectedItems.folders.includes(folder.id)}
                  onSelectionChange={(selected) => 
                    handleItemSelection(folder.id, selected, 'folder')
                  }
                />
              </Grid>
            ))}
            {filteredFiles.map((file) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                <FileCard
                file={file}
                listVariant={listVariant}
                onClick={() => handleFileOperation('preview', file.id)}
                onOperation={handleFileOperation}
                  isSelected={uiSelectedItems.files.includes(file.id)}
                  onSelectionChange={(selected) => 
                    handleItemSelection(file.id, selected, 'file')
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  // Update handleKeyDown to use uiSelectedItems
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't handle shortcuts when focus is in an input field
    if (
      document.activeElement &&
      (document.activeElement.tagName === 'INPUT' ||
        document.activeElement.tagName === 'TEXTAREA')
    ) {
      return;
    }

    // Ctrl+V to paste
    if (event.ctrlKey && event.key === 'v') {
      event.preventDefault();
      handlePasteFromClipboard();
    }
    
    // Ctrl+C to copy selected
    if (event.ctrlKey && event.key === 'c') {
      event.preventDefault();
      if (uiSelectedItems.files.length > 0 || uiSelectedItems.folders.length > 0) {
        handleBatchOperation('copy');
      }
    }
    
    // Ctrl+X to cut selected
    if (event.ctrlKey && event.key === 'x') {
      event.preventDefault();
      if (uiSelectedItems.files.length > 0 || uiSelectedItems.folders.length > 0) {
        handleBatchOperation('cut');
      }
    }
    
    // Ctrl+A to select all
    if (event.ctrlKey && event.key === 'a') {
      event.preventDefault();
      handleSelectAll();
    }
    
    // Delete key to delete selected
    if (event.key === 'Delete') {
      event.preventDefault();
      if (uiSelectedItems.files.length > 0 || uiSelectedItems.folders.length > 0) {
        handleBatchOperation('delete');
      }
    }
  }, [dispatch, uiSelectedItems, handleBatchOperation, handlePasteFromClipboard, handleSelectAll]);

  // Add this useEffect for keyboard shortcuts
  useEffect(() => {
    // Add keyboard event listeners
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Global shortcut listener — stable deps via refs to avoid rebind on every contents update
  useEffect(() => {
    const handler = (e: any) => {
      const action = e.detail?.action;
      switch (action) {
        case 'refresh':
          forceRefreshContentsRef.current();
          break;
        case 'toggle-view':
          handleViewModeChangeRef.current(
            null,
            viewOptionsRef.current === 'grid' ? 'list' : 'grid'
          );
          break;
        case 'select-all':
          handleSelectAllRef.current();
          break;
        case 'search':
          searchInputRef.current?.focus();
          break;
        case 'new-folder':
          setIsNewFolderDialogOpen(true);
          break;
        case 'upload':
          requestUpload();
          break;
        case 'copy': {
          const { files, folders } = uiSelectedItemsRef.current;
          const contents = currentFolderContentsRef.current;
          const items: ClipboardItem[] = [];

          files.forEach(fileId => {
            const file = contents?.files.find(f => f.id === fileId);
            if (file) {
              items.push({ id: fileId, type: 'file', name: file.name || 'Unknown file' });
            }
          });

          folders.forEach(selectedFolderId => {
            const folder = contents?.folders.find(f => f.id === selectedFolderId);
            if (folder) {
              items.push({ id: selectedFolderId, type: 'folder', name: folder.name || 'Unknown folder' });
            }
          });

          if (items.length > 0) {
            dispatch(copyToClipboard({ items, sourceFolder: folderId || 'root' }));
            enqueueSnackbar(`Copied ${items.length} item${items.length > 1 ? 's' : ''} to clipboard`, { variant: 'success' });
          } else {
            enqueueSnackbar('No items selected', { variant: 'info' });
          }
          break;
        }
        case 'cut': {
          const { files, folders } = uiSelectedItemsRef.current;
          const contents = currentFolderContentsRef.current;
          const items: ClipboardItem[] = [];

          files.forEach(fileId => {
            const file = contents?.files.find(f => f.id === fileId);
            if (file) {
              items.push({ id: fileId, type: 'file', name: file.name || 'Unknown file' });
            }
          });

          folders.forEach(selectedFolderId => {
            const folder = contents?.folders.find(f => f.id === selectedFolderId);
            if (folder) {
              items.push({ id: selectedFolderId, type: 'folder', name: folder.name || 'Unknown folder' });
            }
          });

          if (items.length > 0) {
            dispatch(cutToClipboard({ items, sourceFolder: folderId || 'root' }));
            enqueueSnackbar(`Cut ${items.length} item${items.length > 1 ? 's' : ''} to clipboard`, { variant: 'success' });
          } else {
            enqueueSnackbar('No items selected', { variant: 'info' });
          }
          break;
        }
        case 'paste':
          handlePasteFromClipboardRef.current();
          break;
        case 'delete': {
          const selection = uiSelectedItemsRef.current;
          if (selection.files.length > 0 || selection.folders.length > 0) {
            handleBatchOperationRef.current('delete');
          } else {
            enqueueSnackbar('No items selected', { variant: 'info' });
          }
          break;
        }
        case 'rename': {
          const { files, folders } = uiSelectedItemsRef.current;
          const contents = currentFolderContentsRef.current;

          if (files.length === 1) {
            const fileId = files[0];
            const file = contents?.files.find(f => f.id === fileId);
            if (file) {
              setItemToRename({
                id: fileId,
                name: file.name || 'Unknown file',
                type: 'file'
              });
              setRenameDialogOpen(true);
            }
          } else if (folders.length === 1) {
            const selectedFolderId = folders[0];
            const folder = contents?.folders.find(f => f.id === selectedFolderId);
            if (folder) {
              setItemToRename({
                id: selectedFolderId,
                name: folder.name || 'Unknown folder',
                type: 'folder'
              });
              setRenameDialogOpen(true);
            }
          } else if (files.length === 0 && folders.length === 0) {
            enqueueSnackbar('No item selected', { variant: 'info' });
          } else {
            enqueueSnackbar('Please select a single item to rename', { variant: 'warning' });
          }
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener('shortcut', handler);
    return () => {
      window.removeEventListener('shortcut', handler);
    };
  }, [folderId, dispatch, enqueueSnackbar]);
  // Handler for blank-space right-click to open create folder menu
  const handleBlankContextMenu = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault();
    setBlankMenuPosition({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
  };

  return (
    <Box sx={{ maxWidth: '100%', overflow: 'hidden' }} onContextMenu={handleBlankContextMenu}>
      {/* Enhanced UI with better header, breadcrumbs, and organization */}
      {renderHeader()}
      
      {isSelectionMode && uiSelectedItems.files.length + uiSelectedItems.folders.length > 0 && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            p: 1,
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
            <Typography variant="body2">
              <strong>{uiSelectedItems.files.length + uiSelectedItems.folders.length}</strong> items selected
            </Typography>
            <Button 
              variant="text" 
              size="small"
              onClick={() => dispatch(clearUiSelection())}
            >
              Clear
            </Button>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            p: { xs: 1, sm: 0 },
            justifyContent: { xs: 'space-between', sm: 'flex-end' }
          }}>
            <Button
              startIcon={<DownloadOutlinedIcon />}
              size="small"
              onClick={() => handleBatchOperation('download')}
            >
              Download
            </Button>
            <Button
              startIcon={<ShareIcon />}
              size="small"
              onClick={() => handleBatchOperation('share')}
            >
              Share
            </Button>
            <Button
              startIcon={<CopyOutlinedIcon />}
              size="small"
              onClick={() => handleBatchOperation('copy')}
            >
              Copy
            </Button>
            <Button
              startIcon={<MoveOutlinedIcon />}
              size="small"
              onClick={() => handleBatchOperation('move')}
            >
              Move
            </Button>
            <Button
              startIcon={<StarOutlineIcon />}
              size="small"
              onClick={() => handleBatchOperation('star')}
            >
              Star
            </Button>
            <Button
              startIcon={<DeleteOutlinedIcon />}
              size="small"
              color="error"
              onClick={() => handleBatchOperation('delete')}
            >
              Delete
            </Button>
          </Box>
        </Paper>
      )}

      <Box sx={{ position: 'relative', pb: isSelectionMode ? { xs: 12, sm: 7 } : 0 }}>
        {renderContent()}
      </Box>

      {/* New Folder Dialog */}
      <NewFolderDialog
        open={isNewFolderDialogOpen}
        onClose={() => setIsNewFolderDialogOpen(false)}
        parentId={effectiveUploadFolderId ?? folderId}
        onFolderCreated={forceRefreshContents}
      />
      
      {/* Quick action button for uploading */}
      {filesView !== 'trash' && (
      <Fab 
        color="primary" 
        aria-label="upload"
        onClick={requestUpload}
        sx={{ 
          position: 'fixed',
          right: 20,
          bottom: isSelectionMode ? 80 : 20,
          zIndex: 999,
          transition: 'bottom 0.3s ease'
        }}
      >
        <FileUploadIcon />
      </Fab>
      )}

      {/* Share Dialog */}
      {shareItem && (
        <ShareDialog
          open={isShareDialogOpen}
          onClose={() => {
            setIsShareDialogOpen(false);
            setShareItem(null);
            // Refresh the folder contents after sharing
            loadFolderContents();
          }}
          itemId={shareItem.id}
          itemName={shareItem.name}
          itemType={shareItem.type}
        />
      )}

      {/* Add FolderDownloadDialog component */}
      <FolderDownloadDialogComponent />

      {/* Rename Dialog */}
      <RenameDialog
        open={renameDialogOpen}
        onClose={() => {
          setRenameDialogOpen(false);
          setItemToRename(null);
        }}
        item={itemToRename}
        onSuccess={forceRefreshContents}
      />

      <ItemPropertiesDialog
        open={propertiesDialogOpen}
        onClose={() => {
          setPropertiesDialogOpen(false);
          setItemForProperties(null);
        }}
        item={itemForProperties}
      />

      {/* Blank-area context menu */}
      <Menu
        open={Boolean(blankMenuPosition)}
        onClose={() => setBlankMenuPosition(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          blankMenuPosition !== null
            ? { top: blankMenuPosition.mouseY, left: blankMenuPosition.mouseX }
            : undefined
        }
      >
        {/* Paste option when clipboard has items */}
        {clipboard.items?.length > 0 && clipboard.actionType && (
          <>
            <MenuItem onClick={() => { handlePasteFromClipboard(); setBlankMenuPosition(null); }}>
              Paste
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem
          onClick={() => {
            setIsNewFolderDialogOpen(true);
            setBlankMenuPosition(null);
          }}
        >
          Create Folder
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FileBrowser; 